import React, { useState, useRef } from "react";
import { Icon } from "@iconify/react";
import { decodeAuthToken, HStack, VStack } from "../../../components/utils";
import { Input, Label } from "../../../components/shared/InputField";
import { ScheduleParams, ScheduleType } from "../../../types/common";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { AssociatedUsers } from "../../../utils/interfaces/Companies";
import { useAuthStore } from "../../../store";

interface ScheduleSectionProps {
  value: ScheduleParams | undefined;
  onChange: (params: ScheduleParams | undefined) => void;
  users: AssociatedUsers[];
}

const ORDINAL_SUFFIXES: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
function ordinal(n: number): string {
  const suffix = ORDINAL_SUFFIXES[n] ?? "th";
  return `${n}${suffix}`;
}

const DATE_FMT: Intl.DateTimeFormatOptions = {
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
};

function formatNextRun(
  scheduleType: ScheduleType,
  intervalDays?: number,
  monthlyDay?: number,
  startDate?: string
): string {
  const now = new Date();
  const startBasis = startDate ? new Date(startDate) : null;
  const useFutureStart = startBasis !== null && startBasis > now;

  if (scheduleType === "INTERVAL") {
    if (!intervalDays || intervalDays <= 0) return "";
    if (useFutureStart)
      return startBasis!.toLocaleDateString(undefined, DATE_FMT);
    const next = new Date();
    next.setDate(next.getDate() + intervalDays);
    return next.toLocaleDateString(undefined, DATE_FMT);
  }

  // MONTHLY_DATE — clamp to the actual last day of whichever month is used
  if (!monthlyDay) return "";
  function clampedDate(year: number, month: number): Date {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(monthlyDay!, lastDay));
  }
  const basis = useFutureStart ? startBasis! : now;
  const bYear = basis.getFullYear();
  const bMonth = basis.getMonth();
  const candidate = clampedDate(bYear, bMonth);
  const first =
    candidate >= basis
      ? candidate
      : clampedDate(bMonth === 11 ? bYear + 1 : bYear, (bMonth + 1) % 12);
  return first.toLocaleDateString(undefined, DATE_FMT);
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
  value,
  onChange,
  users: associatedUsers,
}) => {
  const [enabled, setEnabled] = useState(!!value);
  const toggleId = useRef(
    `schedule-toggle-${Math.random().toString(36).slice(2)}`
  ).current;
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    value?.scheduleType ?? "INTERVAL"
  );

  const recipients = value?.recipients ?? [];
  const intervalDays = value?.intervalDays ?? 10;
  const monthlyDay = value?.monthlyDay ?? 1;
  const [rawDays, setRawDays] = useState<string>(String(intervalDays));
  const [rawMonthlyDay, setRawMonthlyDay] = useState<string>(
    String(monthlyDay)
  );
  const [startDate, setStartDate] = useState<string>(value?.startDate ?? "");

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    if (!next) {
      onChange(undefined);
    } else {
      onChange(
        buildParams(
          scheduleType,
          recipients,
          Number(rawDays),
          Number(rawMonthlyDay),
          startDate
        )
      );
    }
  };

  const { data: users } = useFilteredAssociatedUsers(associatedUsers);

  function buildParams(
    type: ScheduleType,
    rcpts: string[],
    iDays: number,
    mDay: number,
    sDate: string
  ): ScheduleParams {
    const startDateParam = sDate ? { startDate: sDate } : {};
    if (type === "MONTHLY_DATE") {
      return {
        recipients: rcpts,
        scheduleType: type,
        monthlyDay: mDay,
        ...startDateParam,
      };
    }
    return {
      recipients: rcpts,
      scheduleType: type,
      intervalDays: iDays,
      ...startDateParam,
    };
  }

  const handleScheduleTypeChange = (type: ScheduleType) => {
    setScheduleType(type);
    if (enabled) {
      onChange(
        buildParams(
          type,
          recipients,
          Number(rawDays),
          Number(rawMonthlyDay),
          startDate
        )
      );
    }
  };

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setRawDays(raw);
  };

  const handleIntervalBlur = () => {
    const parsed = parseInt(rawDays, 10);
    const days = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
    setRawDays(String(days));
    if (enabled) {
      onChange(
        buildParams(
          scheduleType,
          recipients,
          days,
          Number(rawMonthlyDay),
          startDate
        )
      );
    }
  };

  const handleMonthlyDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setRawMonthlyDay(raw);
  };

  const handleMonthlyDayBlur = () => {
    const parsed = parseInt(rawMonthlyDay, 10);
    const day = Number.isNaN(parsed) || parsed < 1 ? 1 : Math.min(parsed, 31);
    setRawMonthlyDay(String(day));
    if (enabled) {
      onChange(
        buildParams(scheduleType, recipients, Number(rawDays), day, startDate)
      );
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setStartDate(val);
    if (enabled) {
      onChange(
        buildParams(
          scheduleType,
          recipients,
          Number(rawDays),
          Number(rawMonthlyDay),
          val
        )
      );
    }
  };

  const handleClearStartDate = () => {
    setStartDate("");
    if (enabled) {
      onChange(
        buildParams(
          scheduleType,
          recipients,
          Number(rawDays),
          Number(rawMonthlyDay),
          ""
        )
      );
    }
  };

  const userOptions = users.map((u) => ({ value: u.name }));
  const selectedNames = users
    .filter((u) => u.userId && recipients.includes(String(u.userId)))
    .map((u) => u.name);

  const handleUserSelection = (names: string[]) => {
    const ids = users
      .filter((u) => names.includes(u.name) && u.userId)
      .map((u) => String(u.userId));
    onChange(
      buildParams(
        scheduleType,
        ids,
        Number(rawDays),
        Number(rawMonthlyDay),
        startDate
      )
    );
  };

  const nextRunLabel = enabled
    ? formatNextRun(
        scheduleType,
        Number(rawDays),
        Number(rawMonthlyDay),
        startDate
      )
    : "";

  return (
    <VStack className="w-full gap-3">
      {/* Toggle row */}
      <HStack className="items-center gap-2">
        <input
          type="checkbox"
          id={toggleId}
          checked={enabled}
          onChange={handleToggle}
          className="accent-orange-501 w-4 h-4 cursor-pointer"
        />
        <label
          htmlFor={toggleId}
          className="text-sm font-medium text-gray-700 cursor-pointer select-none"
        >
          Schedule this report
        </label>
      </HStack>

      {/* Schedule fields */}
      {enabled && (
        <VStack className="gap-4 pl-6 border-l-2 border-orange-200">
          {/* Recipients */}
          <VStack className="gap-1">
            <Label className="text-sm font-normal">Recipients</Label>
            <MultiSelectV2
              _className="w-full"
              multiple
              placeholder="-- Select users --"
              options={userOptions}
              optionValues={selectedNames}
              handleSelection={handleUserSelection}
            />
          </VStack>

          {/* Schedule type selector */}
          <VStack className="gap-1">
            <Label className="text-sm font-normal">Schedule type</Label>
            <HStack className="gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                <input
                  type="radio"
                  name="scheduleType"
                  value="INTERVAL"
                  checked={scheduleType === "INTERVAL"}
                  onChange={() => handleScheduleTypeChange("INTERVAL")}
                  className="accent-orange-501"
                />
                Every N days
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                <input
                  type="radio"
                  name="scheduleType"
                  value="MONTHLY_DATE"
                  checked={scheduleType === "MONTHLY_DATE"}
                  onChange={() => handleScheduleTypeChange("MONTHLY_DATE")}
                  className="accent-orange-501"
                />
                Day of every month
              </label>
            </HStack>
          </VStack>

          {/* Row 1 — schedule-specific field */}
          <HStack className="gap-6 items-start">
            {scheduleType === "INTERVAL" ? (
              <VStack className="gap-1 w-56">
                <Label className="text-sm font-normal">
                  Repeat every (days)
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={rawDays}
                  onChange={handleIntervalChange}
                  onBlur={handleIntervalBlur}
                  className="w-full"
                />
              </VStack>
            ) : (
              <VStack className="gap-1 w-56">
                <Label className="text-sm font-normal">
                  Day of month (1–31)
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={rawMonthlyDay}
                  onChange={handleMonthlyDayChange}
                  onBlur={handleMonthlyDayBlur}
                  className="w-full"
                  placeholder="e.g. 2"
                />
                {Number(rawMonthlyDay) >= 1 && Number(rawMonthlyDay) <= 31 && (
                  <span className="text-xs text-gray-400">
                    Runs on the {ordinal(Number(rawMonthlyDay))} of every month
                  </span>
                )}
              </VStack>
            )}
          </HStack>

          {/* Row 2 — starting from */}
          <HStack className="gap-6 items-end">
            <VStack className="gap-1">
              <Label className="text-sm font-normal">
                Starting from&nbsp;
                <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                min={new Date().toISOString().slice(0, 10)}
                className="w-56"
              />
            </VStack>
            {startDate && (
              <button
                type="button"
                onClick={handleClearStartDate}
                className="mb-1 text-xs text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            )}
          </HStack>

          {/* Row 3 — next execution preview */}
          {nextRunLabel && (
            <HStack className="items-center gap-1 text-xs text-gray-500">
              <Icon
                icon="mdi:clock-outline"
                width={14}
                className="text-orange-400"
              />
              <span>
                Next execution:{" "}
                <span className="font-medium text-gray-700">
                  {nextRunLabel}
                </span>
              </span>
            </HStack>
          )}
        </VStack>
      )}
    </VStack>
  );
};

const INTERNAL_DOMAINS = ["rulezero.com", "hissa.com"];
const EXCLUSION_LIST = INTERNAL_DOMAINS.map((domain) => `@${domain}`);

/**
 * Returns associated users, filtering out `@xyz.com` accounts
 * unless the currently logged-in user's email is itself an `@xyz.com` address.
 */
export function useFilteredAssociatedUsers(users: AssociatedUsers[]) {
  const { accessToken } = useAuthStore();

  const decodedEmail: string = accessToken
    ? decodeAuthToken(accessToken)?.emailId ?? ""
    : "";

  const isInternalUser = INTERNAL_DOMAINS.some((domain) =>
    decodedEmail.toLowerCase().endsWith(`@${domain}`)
  );

  const data = users
    ? isInternalUser
      ? users
      : users.filter(
          (u) =>
            !EXCLUSION_LIST.some((domain) =>
              u.email.toLowerCase().endsWith(domain)
            )
        )
    : users;

  return { data };
}

export default ScheduleSection;
